import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { deviceAPI, settingsAPI } from '../services/api';

const EditDeviceDialog = ({ isOpen, onClose, device, onSave, user }) => {
  const isEmployee = user?.role === 'employee';
  const editableFieldsForEmployee = ['osVersion', 'condition', 'usageType', 'description'];
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [deliveryUnits, setDeliveryUnits] = useState([]);
  const [registeredTeamIds, setRegisteredTeamIds] = useState([]);
  const [formData, setFormData] = useState({
    assetId: '',
    deviceName: '',
    deviceType: '',
    model: '',
    osVersion: '',
    color: '',
    ram: '',
    displaySize: '',
    storage: '',
    chipset: '',
    udid: '',
    serialNumber: '',
    arSupport: 'Not Available',
    screenResolution: '',
    condition: 'GOOD',
    purchaseDate: '',
    purchasedFrom: '',
    purchaseStatus: '',
    registeredTeamId: '',
    usageType: '',
    description: '',
    deliveryUnit: 'DU1'
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && device) {
      // Populate form with device data ONLY - no default values
      setFormData({
        assetId: device.assetId || '',
        deviceName: device.deviceName || '',
        deviceType: device.deviceType || '',
        model: device.model || '',
        osVersion: device.osVersion || '',
        color: device.color || '',
        ram: device.ram || '',
        displaySize: device.displaySize || '',
        storage: device.storage || '',
        chipset: device.chipset || '',
        udid: device.udid || '',
        serialNumber: device.serialNumber || '',
        arSupport: device.arSupport || '',
        screenResolution: device.screenResolution || '',
        condition: device.condition || '',
        purchaseDate: device.purchaseDate || '',
        purchasedFrom: device.purchasedFrom || '',
        purchaseStatus: device.purchaseStatus || device.purchaseCondition || '',
        registeredTeamId: device.registeredTeamId || '',
        usageType: device.usageType || '',
        description: device.description || '',
        deliveryUnit: device.deliveryUnit || ''
      });
      setMessage({ type: '', text: '' });
    }
  }, [isOpen, device]);

  useEffect(() => {
    if (isOpen) {
      loadDeviceTypes();
      loadDeliveryUnits();
      loadRegisteredTeamIds();
    }
  }, [isOpen]);

  // Note: Auto-fill removed from edit dialog to preserve database values

  const loadDeviceTypes = async () => {
    try {
      const response = await settingsAPI.getDeviceTypes();
      const types = Array.isArray(response.data) ? response.data : (response.data?.deviceTypes || []);
      setDeviceTypes(types);
    } catch (error) {
      console.error('Failed to load device types:', error);
      const defaultTypes = ['iPhone', 'Android Phone', 'iPad', 'Tablet', 'Laptop', 'Printer', 'Camera', 'Other'];
      setDeviceTypes(defaultTypes);
    }
  };

  const loadDeliveryUnits = async () => {
    try {
      const response = await settingsAPI.getDeliveryUnits();
      const units = Array.isArray(response.data) ? response.data : (response.data?.deliveryUnits || []);
      setDeliveryUnits(units);
    } catch (error) {
      console.error('Failed to load delivery units:', error);
      const defaultUnits = ['DU1', 'DU2', 'DU3', 'DU4', 'DU5'];
      setDeliveryUnits(defaultUnits);
    }
  };

  const loadRegisteredTeamIds = async () => {
    try {
      const response = await settingsAPI.getRegisteredTeamIds();
      const teamIds = Array.isArray(response.data) ? response.data : (response.data?.registeredTeamIds || []);
      setRegisteredTeamIds(teamIds);
    } catch (error) {
      console.error('Failed to load registered team IDs:', error);
      setRegisteredTeamIds([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // For employees, only send editable fields
      const dataToSend = isEmployee 
        ? {
            osVersion: formData.osVersion,
            condition: formData.condition,
            usageType: formData.usageType,
            description: formData.description
          }
        : formData;
      
      await deviceAPI.update(device.assetId, dataToSend);
      setMessage({ type: 'success', text: 'Device updated successfully!' });
      setTimeout(() => {
        onSave();
        onClose();
      }, 1000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update device'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div 
      className="edit-dialog-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 10001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowY: 'auto',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        className="edit-dialog-content"
        style={{
          backgroundColor: 'white',
          padding: '25px',
          borderRadius: '12px',
          minWidth: '800px',
          maxWidth: '1200px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ margin: 0, color: '#7c3aed', fontSize: '24px' }}>Edit Device</h2>
            {isEmployee && (
              <span style={{
                backgroundColor: '#fef3c7',
                color: '#92400e',
                padding: '4px 12px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                border: '1px solid #fbbf24'
              }}>
                📝 Limited Edit Mode
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f3f4f6';
              e.target.style.color = '#333';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#666';
            }}
          >
            ×
          </button>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type === 'info' ? 'success' : message.type}`} style={{ marginBottom: '0.75rem' }}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)', 
            gap: '0.75rem',
            marginBottom: '0.75rem'
          }}>
            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-deliveryUnit" style={{ marginBottom: '0.25rem' }}>Delivery Unit *</label>
              <select
                id="edit-deliveryUnit"
                name="deliveryUnit"
                value={formData.deliveryUnit || 'DU1'}
                onChange={handleChange}
                required
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              >
                {deliveryUnits.map((unit, index) => (
                  <option key={index} value={unit}>{unit}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-deviceType" style={{ marginBottom: '0.25rem' }}>Device Type *</label>
              <select
                id="edit-deviceType"
                name="deviceType"
                value={formData.deviceType}
                onChange={handleChange}
                required
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              >
                <option value="">Select type</option>
                {deviceTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-deviceName" style={{ marginBottom: '0.25rem' }}>Device Name *</label>
              <input
                type="text"
                id="edit-deviceName"
                name="deviceName"
                value={formData.deviceName}
                onChange={handleChange}
                required
                placeholder="iPhone 14 Pro"
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-assetId" style={{ marginBottom: '0.25rem' }}>Asset ID *</label>
              <input
                type="text"
                id="edit-assetId"
                name="assetId"
                value={formData.assetId}
                onChange={handleChange}
                required
                placeholder="ASSET-001"
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-model" style={{ marginBottom: '0.25rem' }}>Model *</label>
              <input
                type="text"
                id="edit-model"
                name="model"
                value={formData.model}
                onChange={handleChange}
                required
                placeholder="iPhone 14 Pro Max, Link4 P0"
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-osVersion" style={{ marginBottom: '0.25rem' }}>
                OS Version *
                {isEmployee && <span style={{ color: '#10b981', marginLeft: '4px', fontSize: '12px' }}>✓ Editable</span>}
              </label>
              <input
                type="text"
                id="edit-osVersion"
                name="osVersion"
                value={formData.osVersion}
                onChange={handleChange}
                required
                placeholder="iOS 17.0"
                style={isEmployee ? { borderColor: '#10b981', borderWidth: '2px' } : {}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-color" style={{ marginBottom: '0.25rem' }}>Color</label>
              <input
                type="text"
                id="edit-color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                placeholder="Space Black"
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-ram" style={{ marginBottom: '0.25rem' }}>RAM</label>
              <input
                type="text"
                id="edit-ram"
                name="ram"
                value={formData.ram}
                onChange={handleChange}
                placeholder={['Instax Printer', 'Instax Camera', 'Digital Camera'].includes(formData.deviceType) ? 'NA' : '8GB'}
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-displaySize" style={{ marginBottom: '0.25rem' }}>Display Size</label>
              <input
                type="text"
                id="edit-displaySize"
                name="displaySize"
                value={formData.displaySize}
                onChange={handleChange}
                placeholder="6.1 inches"
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-storage" style={{ marginBottom: '0.25rem' }}>Storage</label>
              <input
                type="text"
                id="edit-storage"
                name="storage"
                value={formData.storage}
                onChange={handleChange}
                placeholder="256GB"
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-chipset" style={{ marginBottom: '0.25rem' }}>Chipset</label>
              <input
                type="text"
                id="edit-chipset"
                name="chipset"
                value={formData.chipset}
                onChange={handleChange}
                placeholder="A17 Pro"
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-screenResolution" style={{ marginBottom: '0.25rem' }}>Screen Resolution</label>
              <input
                type="text"
                id="edit-screenResolution"
                name="screenResolution"
                value={formData.screenResolution}
                onChange={handleChange}
                placeholder={['Instax Printer', 'Instax Camera', 'Digital Camera'].includes(formData.deviceType) ? 'NA' : '2556x1179'}
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-udid" style={{ marginBottom: '0.25rem' }}>UDID (Optional)</label>
              <input
                type="text"
                id="edit-udid"
                name="udid"
                value={formData.udid}
                onChange={handleChange}
                placeholder="Unique Device ID"
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-serialNumber" style={{ marginBottom: '0.25rem' }}>Serial Number (Optional)</label>
              <input
                type="text"
                id="edit-serialNumber"
                name="serialNumber"
                value={formData.serialNumber}
                onChange={handleChange}
                placeholder="Device Serial Number"
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              />
            </div>
          </div>

          {/* Purchase Information Section */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)', 
            gap: '0.75rem',
            marginBottom: '0.75rem',
            paddingTop: '0.5rem',
            borderTop: '1px solid #e0e4e7'
          }}>
            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-purchaseDate" style={{ marginBottom: '0.25rem' }}>Purchase Date</label>
              <input
                type="date"
                id="edit-purchaseDate"
                name="purchaseDate"
                value={formData.purchaseDate}
                onChange={handleChange}
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-purchasedFrom" style={{ marginBottom: '0.25rem' }}>Purchased From</label>
              <input
                type="text"
                id="edit-purchasedFrom"
                name="purchasedFrom"
                value={formData.purchasedFrom}
                onChange={handleChange}
                placeholder="Store/Vendor name"
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-purchaseStatus" style={{ marginBottom: '0.25rem' }}>Purchase Status</label>
              <select
                id="edit-purchaseStatus"
                name="purchaseStatus"
                value={formData.purchaseStatus || ''}
                onChange={handleChange}
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              >
                <option value="">Select status</option>
                <option value="New">New</option>
                <option value="Used">Used</option>
                <option value="CustomerSupplied">CustomerSupplied</option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-registeredTeamId" style={{ marginBottom: '0.25rem' }}>Registered Team ID</label>
              <select
                id="edit-registeredTeamId"
                name="registeredTeamId"
                value={formData.registeredTeamId || ''}
                onChange={handleChange}
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              >
                <option value="">-- Select Team ID --</option>
                {registeredTeamIds.map((teamId, index) => (
                  <option key={index} value={teamId}>{teamId}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Condition and Other Fields Section */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)', 
            gap: '0.75rem',
            marginBottom: '0.75rem',
            paddingTop: '0.5rem',
            borderTop: '1px solid #e0e4e7'
          }}>
            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-condition" style={{ marginBottom: '0.25rem' }}>
                Condition *
                {isEmployee && <span style={{ color: '#10b981', marginLeft: '4px', fontSize: '12px' }}>✓ Editable</span>}
              </label>
              <select
                id="edit-condition"
                name="condition"
                value={formData.condition || 'GOOD'}
                onChange={handleChange}
                required
                style={isEmployee ? { borderColor: '#10b981', borderWidth: '2px' } : {}}
              >
                <option value="GOOD">GOOD</option>
                <option value="BAD">BAD</option>
                <option value="Retired">Retired</option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-arSupport" style={{ marginBottom: '0.25rem' }}>AR Support *</label>
              <select
                id="edit-arSupport"
                name="arSupport"
                value={formData.arSupport || 'Not Available'}
                onChange={handleChange}
                required
                disabled={isEmployee}
                style={isEmployee ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.6 } : {}}
              >
                <option value="Not Available">Not Available</option>
                <option value="Available">Available</option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="edit-usageType" style={{ marginBottom: '0.25rem' }}>
                Usage Type
                {isEmployee && <span style={{ color: '#10b981', marginLeft: '4px', fontSize: '12px' }}>✓ Editable</span>}
              </label>
              <select
                id="edit-usageType"
                name="usageType"
                value={formData.usageType || ''}
                onChange={handleChange}
                style={isEmployee ? { borderColor: '#10b981', borderWidth: '2px' } : {}}
              >
                <option value="">Select usage type</option>
                <option value="Development">Development</option>
                <option value="Testing">Testing</option>
              </select>
            </div>

            <div></div>
          </div>

          {/* Description Section */}
          <div className="form-group" style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="edit-description" style={{ marginBottom: '0.25rem' }}>
              Description
              {isEmployee && <span style={{ color: '#10b981', marginLeft: '4px', fontSize: '12px' }}>✓ Editable</span>}
            </label>
            <textarea
              id="edit-description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Additional notes or description"
              rows="2"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                background: 'var(--light-surface)',
                border: isEmployee ? '2px solid #10b981' : '1px solid rgba(0, 0, 0, 0.2)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem',
                color: 'var(--light-text)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                fontWeight: '500',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button 
              type="button"
              className="btn btn-secondary" 
              onClick={onClose}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none'
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Device'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default EditDeviceDialog;
